<template>
<div>
  <div id="map" style="height: 300px"></div>
  <div class="footnote mt-2">Map of district with outlines of
    <a style="text-decoration: underline;" href="https://en.wikipedia.org/wiki/Media_market">DMA(s)</a>
    ({{ dmas.join(', ') }})
  </div>
  <v-btn id="leaflet-zoom-toggle" @click="zoomEnabled = !zoomEnabled">
    <span v-if="zoomEnabled">Disable Zoom</span>
    <span v-if="!zoomEnabled">Enable Zoom</span>
  </v-btn>
</div>
</template>

<script>
import { mapGetters } from 'vuex'

import L from 'leaflet'
import * as topojson from 'topojson'

const name = 'leaflet_map'

const props = {
  type: {
    type: String,
    required: true
  },
  geoid: {
    required: false
  },
  state: {
    required: false
  }
}

const computed = {
  ...mapGetters({
    dmasByCd: 'geojson/dmasByCd',
    dmasByState: 'geojson/dmasByState',
    statesList: 'elections/states'
  })
}

const methods = {
  drawMap () {
    L.TopoJSON = L.GeoJSON.extend({
      addData: function (jsonData) {
        if (jsonData.type === 'Topology') {
          for (let key in jsonData.objects) {
            let geojson = topojson.feature(jsonData, jsonData.objects[key])
            L.GeoJSON.prototype.addData.call(this, geojson)
          }
        } else {
          L.GeoJSON.prototype.addData.call(this, jsonData)
        }
      }
    })

    let geojson = null
    this.type === 'house' ? geojson = JSON.parse(this.dmasByCd) : geojson = JSON.parse(this.dmasByState)
    const stateFips = this.statesList.find(state => state.name === this.state).id

    function style (feature) {
      return {
        weight: 2,
        color: '#BDBDBD',
        fillOpacity: 0.8,
        fillColor: '#E6E7E8'
      }
    }

    function highlightFeature (e) {
      let layer = e.target

      layer.setStyle({
        weight: 2,
        color: '#BDBDBD',
        fillOpacity: 0.4,
        fillColor: '#E6E7E8'
      })

      if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
        layer.bringToFront()
      }

      layer.bindTooltip(`${layer.feature.properties.DMA}`).openTooltip()
    }

    function resetHighlight (e) {
      layer.resetStyle(e.target)
    }

    function onEachFeature (feature, layer) {
      layer.on({
        mouseover: highlightFeature,
        mouseout: resetHighlight
      })
    }

    const layer = new L.TopoJSON(geojson, {
      filter: (feature) => {
        if (this.type === 'house' && feature.properties.GEOID === this.geoid) {
          this.dmas.push(feature.properties.DMA)
          return true
        } else if (this.type === 'senate' && feature.properties.statefp === stateFips) {
          this.dmas.push(feature.properties.DMA)
          return true
        }
      },
      style: style,
      onEachFeature: onEachFeature
    }).addTo(this.map)

    this.map.fitBounds(layer.getBounds())

    this.map.dragging.disable()
    this.map.touchZoom.disable()
    this.map.doubleClickZoom.disable()
    this.map.scrollWheelZoom.disable()
    this.map.boxZoom.disable()
    this.map.keyboard.disable()
    if (this.map.tap) this.map.tap.disable()
    document.getElementById('map').style.cursor = 'default'
  }
}

export default {
  name,
  props,
  computed,
  methods,
  data () {
    return {
      zoomEnabled: false,
      map: null,
      dmas: [],
      noLabelsLayer: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}.png',
      labelsOnlyLayer: 'https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}.png'
    }
  },
  mounted () {
    const map = L.map('map').setView([37.8, -96], 4)

    this.map = map

    this.map.createPane('labels')
    this.map.getPane('labels').style.zIndex = 650
    this.map.getPane('labels').style.pointerEvents = 'none'

    L.tileLayer(this.noLabelsLayer, {
      type: 'tilelayer'
    }).addTo(this.map)

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}.png', {
      pane: 'labels'
    }).addTo(this.map)

    this.map.attributionControl.remove()

    this.drawMap()
  },
  watch: {
    zoomEnabled () {
      if (this.zoomEnabled) {
        this.map.dragging.enable()
        this.map.touchZoom.enable()
        this.map.doubleClickZoom.enable()
        this.map.scrollWheelZoom.enable()
        this.map.boxZoom.enable()
        this.map.keyboard.enable()
        if (this.map.tap) this.map.tap.enable()
        document.getElementById('map').style.cursor = 'grab'
      } else {
        this.map.dragging.disable()
        this.map.touchZoom.disable()
        this.map.doubleClickZoom.disable()
        this.map.scrollWheelZoom.disable()
        this.map.boxZoom.disable()
        this.map.keyboard.disable()
        if (this.map.tap) this.map.tap.disable()
        document.getElementById('map').style.cursor = 'default'
      }
    }
  }
}
</script>

<style>
#map {
  width: 100%;
  height: 100%;
}

.leaflet-pane .leaflet-tooltip-pane {
  z-index: 1000;
}

.leaflet-top {
  z-index: 499;
}
</style>



// WEBPACK FOOTER //
// src/components/charts/LeafletMap.vue